// Package imports:
import React, { useState, useRef } from 'react';
import OverlayTrigger, { OverlayTriggerType } from 'react-bootstrap/OverlayTrigger';
import { OverlayInjectedProps } from 'react-bootstrap/Overlay';
import BootstrapTooltip from 'react-bootstrap/Tooltip';
import cx from 'classnames';
interface TooltipDelay {
    show: number;
    hide: number;
}

interface IProps {
    text: string | JSX.Element;
    tooltip: string | JSX.Element;
    trigger?: OverlayTriggerType | OverlayTriggerType[];
    noUnderline?: boolean;
    width?: number;
    tableHeader?: boolean;
    className?: string;
    tooltipClassName?: string;
    delay?: TooltipDelay;
    normalText?: boolean;
    fullHeight?: boolean;
}

const Tooltip: React.FC<IProps> = ({
    text,
    tooltip,
    trigger,
    noUnderline,
    width,
    tableHeader = false,
    className,
    tooltipClassName,
    delay = { show: 200, hide: 300 },
    normalText = false,
    fullHeight = false
}) => {
    // State to manually control tooltip visibility
    const [show, setShow] = useState(false);
    const showTimeout = useRef<NodeJS.Timeout | null>(null);
    const hideTimeout = useRef<NodeJS.Timeout | null>(null);

    // Show the tooltip immediately when mouse enters the trigger
    const handleMouseEnter = () => {
        // Cancel pending hide
        if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
            hideTimeout.current = null;
        }

        // Prevent stacking timeouts
        if (showTimeout.current) {
            clearTimeout(showTimeout.current);
        }

        showTimeout.current = setTimeout(() => {
            setShow(true);
        }, delay.show);
    };

    // Hide the tooltip after a delay when mouse leaves the trigger
    // The delay allows time for the user to move their mouse to the tooltip
    const handleMouseLeave = () => {
        // Cancel pending show
        if (showTimeout.current) {
            clearTimeout(showTimeout.current);
            showTimeout.current = null;
        }

        hideTimeout.current = setTimeout(() => {
            setShow(false);
        }, delay.hide);
    };


    // Keep tooltip open when mouse enters the tooltip itself
    const handleTooltipMouseEnter = () => {
        if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
            hideTimeout.current = null;
        }
    };

    const handleTooltipMouseLeave = () => {
        hideTimeout.current = setTimeout(() => {
            setShow(false);
        }, delay.hide);
    };

    // Render function for the Bootstrap tooltip overlay
    const displayTooltip = (props: OverlayInjectedProps) => {
        // Override width prop since it messes with the tooltip position
        props = { ...props, placement: 'auto', width: undefined };

        return (
            <BootstrapTooltip
                // Component gets KCL_ prefix since it is injected at bottom of DOM
                className={cx('KCL_react-tooltip', tooltipClassName)}
                {...props}
                // Add mouse event handlers to keep tooltip open when hovering over it
                onMouseEnter={handleTooltipMouseEnter}
                onMouseLeave={handleTooltipMouseLeave}
            >
                {/* Invisible content for sizing calculations */}
                <div className='invisible-content'>
                    {tooltip}
                </div>
                {/* Actual visible tooltip content */}
                <div className='tooltip-content-wrapper'>
                    {tooltip}
                </div>
            </BootstrapTooltip>
        );
    }

    // Apply custom width styling if specified
    const customStyle = width ? { width: width + 'px', display: 'inline-block' } : {};

    return (
        <span style={customStyle} className={cx("KCL_tooltip", className, { 'normal-text': normalText })}>
            <OverlayTrigger
                // Configure popper to stay within viewport boundaries
                popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }] }}
                // Use manual trigger since we're controlling visibility with state
                trigger={trigger ?? ['hover', 'focus']}
                // Manually control the show state
                show={show}
                overlay={displayTooltip}
            >
                <span
                    className={cx('display-text', { 'no-underline': noUnderline, 'table-header': tableHeader, 'full-height': fullHeight })}
                    // Add mouse event handlers to control tooltip visibility
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    // Also handle focus events for accessibility
                    onFocus={handleMouseEnter}
                    onBlur={handleMouseLeave}
                >
                    {text}
                </span>
            </OverlayTrigger>
        </span>
    )
}

export default Tooltip;